<template>

    <Toast/>

    <MenuFinancialCompact v-if="userStore.user?.roles?.includes('ROLE_ISORISE')" />
    <MenuHealthCompact v-else />

    <div class="px-6 surface-overlay relative">

        <ul class="list-none py-2 px-0 m-0 flex align-items-center font-medium overflow-x-auto w-full">
            <li class="pr-3"><a class="cursor-pointer">Operational Clarity</a></li>
            <li class="px-2"><i class="pi pi-angle-right text-900"></i></li>
            <li class="px-2"><a class="cursor-pointer text-900 white-space-nowrap">Appointments</a></li>
        </ul>

        <div v-show="activeTab === 1" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Manage Appointments</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callNewAppointment" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>New Appointment</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <DataTable
                        v-model:lazyParams="lazyParams"
                        :value="appointments"
                        lazy
                        paginator
                        size="small"
                        :ptOptions="{ mergeSections: true, classNameMergeStrategy: 'replace' }"
                        :pt="{headerCell: { class: 'py-1 px-2' }, bodyCell: { class: 'p-0' }, bodyRow: { class: 'p-0' }}"
                        :rows="10"
                        :totalRecords="totalRecords"
                        :loading="submitted"
                        :rowsPerPageOptions="[10, 20, 50, 100]"
                        :sortMode="'single'"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        filterDisplay="menu"
                        responsiveLayout="scroll"
                        :globalFilterFields="['id', 'name', 'email', 'mobile']"
                        @page="onPage"
                        @sort="onSort"
                        @filter="onFilter">

                        <template #header>
                            <div class="p-grid">
                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedColumns" :options="columnsFullList"
                                                     optionLabel="header" @update:modelValue="onToggleColumns"
                                                     display="chip"
                                                     placeholder="Select Columns"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <Dropdown v-model="selectedStandardFilter" :options="appointmentTypes"
                                                  optionLabel="name" optionValue="code" placeholder="Select a Filter"
                                                  @update:modelValue="onToggleStandardFilters"/>
                                    </div>

                                </div>

                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-filter-slash" label="Clear Search"
                                                class="p-button-outlined p-ml-2" @click="clearFilter()"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-replay" label="Restore to Original View"
                                                class="p-button-outlined" @click="restoreToOriginal()"/>
                                    </div>
                                </div>

                                <div v-show="companyConfiguration.type === 'H'" class="p-col-12 p-md-2">
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search"/>
                                        <InputText v-model="filters['global'].value"
                                                   placeholder="Keyword Search on Client Name, Billing Plan Name, Scheduled, Description or Practitioner Name (Please press enter to search)"
                                                   style="width: 100%"
                                                   @keyup.enter="onSearch"/>

                                    </span>
                                </div>

                                <div v-show="companyConfiguration.type !== 'H'" class="p-col-12 p-md-2">
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search"/>
                                        <InputText v-model="filters['global'].value"
                                                   placeholder="Keyword Search on Client Name, Billing Plan Name, Scheduled or Description (Please press enter to search)"
                                                   style="width: 100%"
                                                   @keyup.enter="onSearch"/>

                                    </span>
                                </div>

                            </div>
                        </template>

                        <template #empty>
                            No appointments found.
                        </template>

                        <template #loading>
                            Loading appointments. Please wait.
                        </template>

                        <Column
                            field="selectAnOption"
                            header=""
                            :headerStyle="{ width: '2rem' }"
                            :bodyStyle="{ width: '2rem', padding: '0.25rem' }">
                            <template #body="{ data, index }">
                                <Button icon="pi pi-ellipsis-v" class="p-button-sm p-button-text p-button-rounded m-0"
                                        @click="$refs['menu' + index].toggle($event)"/>
                                <Menu
                                    :ref="'menu' + index"
                                    :model="getFilteredItems(data)"
                                    :popup="true"/>
                            </template>
                        </Column>

                        <Column :headerStyle="{ width: '2rem' }"
                                :bodyStyle="{ width: '10rem', padding: '0.25rem' }"
                                field="scheduled" header="Scheduled" :sortable="true"/>

                        <Column :headerStyle="{ width: '2rem' }"
                                :bodyStyle="{ width: '10rem', padding: '0.25rem' }"
                                field="status" header="Status" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Status</span>
                                <span v-if="data.status === 'S'"
                                      :class="'status-badge status-' + data.status">Scheduled</span>
                                <span v-if="data.status === 'C'"
                                      :class="'status-badge status-' + data.status">Completed</span>
                                <span v-if="data.status === 'M'"
                                      :class="'status-badge status-' + data.status">Missed</span>
                                <span v-if="data.status === 'I'"
                                      :class="'status-badge status-' + data.status">Invoiced</span>
                                <span v-if="data.status === 'P'"
                                      :class="'status-badge status-' + data.status">Paid</span>
                                <span v-if="data.status === 'E'"
                                      :class="'status-badge status-' + data.status">Pending</span>
                                <span v-if="data.status === 'U'"
                                      :class="'status-badge status-' + data.status">Unpaid</span>
                                <span v-if="data.status === 'B'"
                                      :class="'status-badge status-' + data.status">Booking</span>
                            </template>
                        </Column>

                        <Column :headerStyle="{ width: '2rem' }"
                                :bodyStyle="{ width: '10rem', padding: '0.25rem' }"
                                field="type" header="Type" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Type</span>
                                <span v-if="data.type === 'P'"
                                      :class="'type-badge status-' + data.type">Personal</span>
                                <span v-if="data.type === 'B'"
                                      :class="'type-badge status-' + data.type">Billable</span>
                                <span v-if="data.type === 'N'"
                                      :class="'type-badge status-' + data.type">Non-Billable</span>
                            </template>
                        </Column>

                        <Column :headerStyle="{ width: '2rem' }"
                                :bodyStyle="{ width: '10rem', padding: '0.25rem' }"
                                field="description" header="Description" :sortable="true"/>

                        <template v-for="col in selectedColumns" :key="col.field">

                            <Column :headerStyle="{ width: '2rem' }"
                                    :bodyStyle="{ width: '10rem', padding: '0.25rem' }"
                                    v-if="col.field === 'clientClaimPreference'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Claim Preference</span>
                                    <span v-if="data.clientClaimPreference === 'Y'"
                                          :class="'type-badge status-' + data.clientClaimPreference">Yes</span>
                                    <span v-else
                                          :class="'type-badge status-N'">No</span>
                                </template>
                            </Column>

                            <Column :headerStyle="{ width: '2rem' }"
                                    :bodyStyle="{ width: '10rem', padding: '0.25rem' }"
                                    v-else :field="col.field" :header="col.header" :sortable="col.sortable"/>

                        </template>


                    </DataTable>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 2" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Edit Appointment</h3>

                    <div class="field">
                        <label for="type">Type</label>
                        <Dropdown v-model="appointment.type" :options="appointmentTypes" optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select a Type"/>
                    </div>

                    <div v-show="appointment.type!=='P'" class="field">
                        <label for="client">Client</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="appointment.clientName" :suggestions="filteredClients"
                                      @complete="searchClients($event)" @item-select="onClientSelected"
                                      field="name"/>
                    </div>

                    <div v-show="appointment.type !== 'P' &&
                            clientProvisionComponents.length > 0" class="field">
                        <label for="provisionComponent">Provision Component</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="appointment.clientProvisionComponentName"
                                      :suggestions="filteredProvisionComponents"
                                      @complete="searchProvisionComponentsOnUpdateAppointment($event)" field="fullName"/>
                    </div>

                    <div v-show="appointment.type!=='P'" class="field">
                        <label for="practitioner">Practitioner</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="appointment.practitionerName"
                                      :suggestions="filteredPractitioners"
                                      @complete="searchPractitioners($event)" field="fullName"/>
                    </div>

                    <div v-show="(appointment.type!=='P') && (!appointment.clientProvisionComponentName)" class="field">
                        <label for="billingPlan">Billing Plan</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="appointment.billingPlanName"
                                      :suggestions="filteredBillingPlans"
                                      @complete="searchBillingPlan($event)" field="name"/>
                    </div>

                    <div v-show="appointment.type!=='P'" class="field">
                        <label for="location">Location</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="appointment.locationName" :suggestions="filteredLocations"
                                      @complete="searchLocations($event)" field="name"/>
                    </div>

                    <div class="field">
                        <label for="description">Description</label>
                        <InputText id="description" v-model.trim="appointment.description" required="true"/>
                    </div>

                    <div class="field">
                        <label for="frequency">Frequency</label>
                        <Dropdown :disabled="true" v-model="appointment.frequency" :options="frequencyTypes"
                                  optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select a Frequency"/>
                    </div>

                    <div v-show="appointment.frequency!=='N'" class="field">
                        <label for="frequency">Number of Occurrences</label>
                        <InputNumber :disabled="true" id="horizontal" v-model="appointment.frequencyLength" showButtons
                                     buttonLayout="horizontal"
                                     :step="1"
                                     :min="1"
                                     decrementButtonClass="p-button-danger"
                                     incrementButtonClass="p-button-success"
                                     incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                     mode="decimal"/>
                    </div>

                    <div class="field">
                        <label for="scheduled">Scheduled Date</label>
                        <Calendar id="scheduled" v-model="parsedScheduled" required="true"
                                  :showTime="true"
                                  :showSeconds="false" :showIcon="true" dateFormat="yy-mm-dd"/>

                    </div>

                    <div class="field">
                        <label for="duration">Duration (Minutes)</label>
                        <InputNumber id="horizontal" v-model="appointment.duration" showButtons
                                     buttonLayout="horizontal"
                                     :step="5"
                                     :min="0"
                                     decrementButtonClass="p-button-danger"
                                     incrementButtonClass="p-button-success"
                                     incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                     mode="decimal"/>
                    </div>

                    <div class="field">
                        <label for="status">Status</label>
                        <br>
                        <Dropdown v-model="appointment.status" :options="statusTypes" optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select a Status"/>
                    </div>

                    <div v-show="(appointment.status === 'S')" class="field">
                        <label for="sendCalendarInvites">Do You Wish to Send Calendar Invites?</label>
                        <Dropdown v-model="appointment.sendCalendarInvites" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <div v-show="(appointment.status === 'S')" class="field">
                        <label for="sendReminders">Do You Wish to Send Reminders?</label>
                        <Dropdown v-model="appointment.sendReminders" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <div v-show="(appointment.status === 'S')" class="field">
                        <label for="sendCreationReminder">Do You Wish to Send Creation Reminder?</label>
                        <Dropdown v-model="appointment.sendCreationReminder" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <div v-show="(appointment.status === 'M')" class="field">
                        <label for="sendMissedNotice">Do You Wish to a Missed Appointment Notice?</label>
                        <Dropdown v-model="appointment.sendMissedNotice" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Save" class="mr-2 mb-2" :loading="submitted" @click="editAppointment"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>


        <div v-show="activeTab === 3" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Delete Appointment for {{ this.appointment.description }} on
                        {{ this.appointment.scheduled }} </h3>

                    <div class="field">
                        <label for="deleteConfirmationResponse">Do You Wish to Delete this Appointment?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Save" class="mr-2 mb-2" :loading="submitted" @click="deleteAppointment"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>


        <div v-show="activeTab === 4" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>View Online Meeting Details</h3>

                    <h3>Host URL</h3>
                    <div class="field">
                                    <Textarea v-model.trim="appointment.onlineMeetingHostUrl" :autoResize="true"
                                              :disabled="true" rows="3" cols="50"/>
                    </div>

                    <h3>Guest/Client URL</h3>
                    <Textarea v-model.trim="appointment.onlineMeetingGuestUrl" :autoResize="true"
                              :disabled="true" rows="2" cols="50"/>

                    <br><br>
                    <Button label="Start Meeting" class="mr-2 mb-2" :loading="submitted" @click="startMeeting"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>


        <div v-show="activeTab === 5" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Generate Invoice for Appointment on {{ this.appointment.scheduled }} for
                        {{ this.appointment.clientName }}</h3>

                    <div class="field">
                        <label for="invoiceType">Invoice Type</label>
                        <Dropdown v-model="invoice.type" :options="invoiceTypes"
                                  optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <div class="field">
                        <label for="invoiced">Invoice Date</label>
                        <Calendar id="invoiced" v-model="invoice.invoiced" required="true"
                                  :showTime="true"
                                  :showSeconds="false" :showIcon="true" dateFormat="yy-mm-dd"/>
                    </div>

                    <div v-if="invoice.type === 'C'" class="field">
                        <label for="startDate">Start Date</label>
                        <Calendar id="startDate" v-model="invoice.startDate" required="true"
                                  :showTime="true"
                                  :showSeconds="false" :showIcon="true" dateFormat="yy-mm-dd"/>

                    </div>

                    <div v-if="invoice.type === 'C'" class="field">
                        <label for="endDate">End Date</label>
                        <Calendar id="endDate" v-model="invoice.endDate" required="true"
                                  :showTime="true"
                                  :showSeconds="false" :showIcon="true" dateFormat="yy-mm-dd"/>

                    </div>

                    <div v-if="((bankAccounts) && (bankAccounts.length > 1))" class="field">
                        <label for="bankAccount">Bank Account</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="invoice.bankAccount"
                                      :suggestions="filteredBankAccounts"
                                      @complete="searchBankAccounts($event)" field="accountHolder"/>
                    </div>


                    <div v-if="invoice.type === 'I'" class="field">
                        <label for="billingPlan">Billing Plan</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="invoice.billingPlanName"
                                      :suggestions="filteredBillingPlans"
                                      @complete="searchBillingPlan($event)" field="name"/>
                    </div>

                    <div v-if="(invoice.type === 'I') && (companyConfiguration.type==='H') && (appointment.clientMedicalName !== null)" class="field">
                        <label for="submissionOption">Invoice Submission Method</label>
                        <Dropdown v-model="invoice.submissionMethod" :options="submissionMethod"
                                  optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <div v-if="(invoice.type === 'I') && (companyConfiguration.type==='H') && (appointment.clientMedicalName === null)" class="field">
                        <label for="submissionOption">Invoice Submission Method</label>
                        <Dropdown v-model="invoice.submissionMethod" :options="submissionMethod" :disabled="true"
                                  optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <div v-if="invoice.type === 'I' && (companyConfiguration.type==='H') && (invoice.submissionMethod!=='C')" class="field">
                        <label for="healthInsuranceSubmission">Health Insurance Submission Method</label>
                        <Dropdown v-model="invoice.healthInsuranceSubmission"
                                  :options="healthInsuranceSubmissionMethod"
                                  optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <div v-if="(companyConfiguration.type==='H') && (appointment.clientMedicalName !== null)" class="field">
                        <label for="useSystemProcedureRates">Use System Procedure Rates?</label>
                        <Dropdown v-model="invoice.useSystemProcedureRates" :options="confirmationTypes"
                                  optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <div v-if="(companyConfiguration.type==='H') && (appointment.clientMedicalName === null)" class="field">
                        <label v-tooltip="'Option can only be selected if the client has a medical aid'" for="useSystemProcedureRates">Use System Procedure Rates?</label>
                        <Dropdown v-model="invoice.useSystemProcedureRates" :options="confirmationTypes" :disabled="true"
                                  optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <div v-if="invoice.useSystemProcedureRates==='Y'" class="field">
                        <label for="rateOption">Rate Option?</label>
                        <Dropdown v-model="invoice.rateOption" :options="rateOptionTypes"
                                  optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <div v-if="company && company.taxRegistered === 'Y'" class="field">
                        <label for="useTaxRate">Use Tax Rate?</label>
                        <Dropdown v-model="invoice.useTaxRate" :options="confirmationTypes"
                                  optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <div v-if="(companyConfiguration.type==='H') &&
                        (invoice.submissionMethod!=='C')" class="field">
                        <label for="useAutomaticSubmission">Automatically Submit Invoice and/or Claim?</label>
                        <Dropdown v-model="invoice.useAutomaticSubmission" :options="confirmationTypes"
                                  optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <div v-if="( (companyConfiguration.type!=='H') || (invoice.submissionMethod==='C'))" class="field">
                        <label for="useAutomaticSubmission">Automatically Submit Invoice?</label>
                        <Dropdown v-model="invoice.useAutomaticSubmission" :options="confirmationTypes"
                                  optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <Button label="Create" class="mr-2 mb-2" :loading="submitted" @click="invoiceAppointment"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 6" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Create Appointment</h3>

                    <div class="field">
                        <label for="type">Type</label>
                        <Dropdown v-model="newAppointment.type" :options="appointmentTypes"
                                  optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select a Type"/>
                    </div>

                    <div v-show="newAppointment.type!=='P'" class="field">
                        <label for="client">Client</label>
                        <AutoComplete placeholder="Search" :dropdown="false" :multiple="false"
                                      v-model="newAppointment.client" :suggestions="filteredClients"
                                      @complete="searchClients($event)" @item-select="onClientSelected"
                                      field="name"/>
                    </div>

                    <div v-show="newAppointment.type !== 'P' &&
                            newAppointment.client?.id &&
                            clientProvisionComponents.length > 0" class="field">
                        <label for="practitioner">Provision Component</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="newAppointment.provisionComponent"
                                      :suggestions="filteredProvisionComponents"
                                      @complete="searchProvisionComponentsOnCreateAppointment($event)" field="fullName"/>
                    </div>

                    <div v-if="(newAppointment.type!=='P') && (practitioners) && (practitioners.length > 1)" class="field">
                        <label for="practitioner">Practitioner</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="newAppointment.practitioner"
                                      :suggestions="filteredPractitioners"
                                      @complete="searchPractitioners($event)" field="fullName"/>
                    </div>

                    <div v-show="(newAppointment.type!=='P') && (!newAppointment.provisionComponent)" class="field">
                        <label for="billingPlan">Billing Plan</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="newAppointment.billingPlan"
                                      :suggestions="filteredBillingPlans"
                                      @complete="searchBillingPlan($event)" field="name"/>
                    </div>

                    <div v-show="newAppointment.type!=='P'" class="field">
                        <label for="location">Location</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="newAppointment.location" :suggestions="filteredLocations"
                                      @complete="searchLocations($event)" field="name"/>
                    </div>

                    <div v-show="newAppointment.type=='P'" class="field">
                        <label for="description">Description</label>
                        <InputText id="description" v-model.trim="newAppointment.description"
                                   required="true"/>
                    </div>

                    <div class="field">
                        <label for="frequency">Frequency</label>
                        <Dropdown v-model="newAppointment.frequency" :options="frequencyTypes"
                                  optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select a Frequency"/>
                    </div>

                    <div v-show="newAppointment.frequency!=='N'" class="field">
                        <label for="frequency">Number of Occurrences</label>
                        <InputNumber id="horizontal" v-model="newAppointment.frequencyLength" showButtons
                                     buttonLayout="horizontal"
                                     :step="1"
                                     :min="1"
                                     decrementButtonClass="p-button-danger"
                                     incrementButtonClass="p-button-success"
                                     incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                     mode="decimal"/>
                    </div>

                    <div class="field">
                        <label for="scheduled">Scheduled Date</label>
                        <Calendar id="scheduled" v-model="newAppointment.scheduled" required="true"
                                  :showTime="true"
                                  :showSeconds="false" :showIcon="true" dateFormat="yy-mm-dd"/>

                    </div>

                    <div class="field">
                        <label for="duration">Duration (Minutes)</label>
                        <InputNumber id="horizontal" v-model="newAppointment.duration" showButtons
                                     buttonLayout="horizontal"
                                     :step="5"
                                     :min="0"
                                     decrementButtonClass="p-button-danger"
                                     incrementButtonClass="p-button-success"
                                     incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                     mode="decimal"/>
                    </div>

                    <div class="field">
                        <label for="status">Status</label>
                        <br>
                        <Dropdown v-model="newAppointment.status" :options="statusTypes" optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select a Status"/>
                    </div>

                    <div v-show="(newAppointment.status === 'S')" class="field">
                        <label for="sendCalendarInvites">Do You Wish to Send Calendar Invites?</label>
                        <Dropdown v-model="newAppointment.sendCalendarInvites" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <div v-show="(newAppointment.status === 'S')" class="field">
                        <label for="sendReminders">Do You Wish to Send Reminders?</label>
                        <Dropdown v-model="newAppointment.sendReminders" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <div v-show="(newAppointment.status === 'S')" class="field">
                        <label for="sendCreationReminder">Do You Wish to Send a Creation Reminder?</label>
                        <Dropdown v-model="newAppointment.sendCreationReminder" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <div v-show="(newAppointment.status === 'M')" class="field">
                        <label for="sendMissedNotice">Do You Wish to Send a Missed Appointment
                            Notice?</label>
                        <Dropdown v-model="newAppointment.sendMissedNotice" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <div
                        v-show="((newAppointment.status === 'S') && (this.companyConfiguration.enableOnlineMeetings === 'Y'))"
                        class="field">
                        <label for="setupOnlineMeeting">Do You Wish to Setup a Kitrin Online
                            Meeting?</label>
                        <Dropdown v-model="newAppointment.setupOnlineMeeting" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Create" class="mr-2 mb-2" :disabled="!canCreateAppointment" :loading="submitted"
                            @click="createAppointment"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 7" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h2>Pay Invoice #{{ invoice.uniqueId }} for {{ invoice.clientName }}</h2>

                    <div class="field">
                        <label for="paid">Paid Date</label>
                        <Calendar id="paid" v-model="payment.paid" required="true"
                                  :showTime="true"
                                  :showSeconds="false" :showIcon="true" dateFormat="yy-mm-dd"/>
                    </div>

                    <div class="field">
                        <label for="type">Type</label>
                        <Dropdown v-model="payment.type" :options="paymentTypes" optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select a Type"/>
                    </div>

                    <div class="field">
                        <label for="description">Description</label>
                        <InputText id="description" v-model.trim="payment.description" required="true"/>
                    </div>

                    <Button label="Allocate" class="mr-2 mb-2" :loading="submitted" @click="allocatePayment"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 8" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Create Appointment Note for Client {{ this.appointment.clientName }}</h3>

                    <div class="field">
                        <label for="template">Type</label>
                        <Dropdown v-model="note.noteTemplateId" :options="noteTemplates" optionLabel="subject"
                                  optionValue="id" placeholder="Select a Template"/>
                    </div>

                    <div class="field">
                        <label for="type">Type</label>
                        <Dropdown v-model="note.type" :options="noteTypes" optionLabel="name" optionValue="code"
                                  placeholder="Select a Type"/>
                    </div>

                    <div class="field">
                        <label for="format">Format</label>
                        <Dropdown v-model="note.format" :options="formatTypes" optionLabel="name" optionValue="code"
                                  placeholder="Select a Format"/>
                    </div>

                    <div class="field">
                        <label for="subject">Subject</label>
                        <InputText id="subject" v-model.trim="note.subject" required="true"/>
                    </div>

                    <div v-show="note.format === 'R'" class="field">
                        <label for="description">Description</label>
                        <Editor v-model="note.description" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                <span class="ql-formats">
                                    <button class="ql-bold" v-tooltip.bottom="'Bold'"></button>
                                    <button class="ql-italic" v-tooltip.bottom="'Italic'"></button>
                                    <button class="ql-underline" v-tooltip.bottom="'Underline'"></button>
                                    <button class="ql-strike" v-tooltip.bottom="'Strike'"></button>
                                    <button class="ql-color" v-tooltip.bottom="'Color'"></button>
                                    <button class="ql-background" v-tooltip.bottom="'Color'"></button>
                                    <button class="ql-list" value="bullet" v-tooltip.bottom="'Bulleted List'"></button>
                                    <button class="ql-list" value="ordered" v-tooltip.bottom="'Ordered List'"></button>
                                    <button class="ql-indent" value="-1" v-tooltip.bottom="'Indent -1'"></button>
                                    <button class="ql-indent" value="+1" v-tooltip.bottom="'Indent +1'"></button>
                                    <button class="ql-image" v-tooltip.bottom="'Image'"></button>
                                    <button class="ql-video" v-tooltip.bottom="'Video'"></button>
                                    <button class="ql-link" v-tooltip.bottom="'Link'"></button>
                                </span>

                            </template>
                        </Editor>
                    </div>

                    <div class="field">
                        <label for="sharable">Is this Sharable?</label>
                        <Dropdown v-model="note.sharable" :options="confirmationTypes" optionLabel="name"
                                  optionValue="code" placeholder="Select an Option"/>
                    </div>

                    <div v-if="this.note.sharable === 'Y'" class="field">
                        <label for="editable">Is this Editable by the Client?</label>
                        <Dropdown v-model="note.editable" :options="confirmationTypes" optionLabel="name"
                                  optionValue="code" placeholder="Select an Option"/>
                    </div>

                    <!--                    <div class="field">-->
                    <!--                        <label for="status">Status</label>-->
                    <!--                        <Dropdown v-model="note.status" :options="statusTypes" optionLabel="name" optionValue="code"-->
                    <!--                                  placeholder="Select an Option"/>-->
                    <!--                    </div>-->

                    <Button label="Create" class="mr-2 mb-2" :loading="submitted" @click="createNote"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>d
        </div>

    </div>

    <IsoEvolveFooter/>

</template>

<script>
import IsoEvolveFooter from "@/components/heading/authentication/IsoEvolveFooter.vue";
import MenuHealthCompact from "@/components/menu/health/MenuHealthCompact.vue";

import {useUserStore} from "@/stores/UserStore";
import UserService from "@/service/UserService";
import {FilterMatchMode, FilterOperator} from "primevue/api";
import moment from 'moment-timezone';

import PractitionerService from "@/service/PractitionerService";
import BillingPlanService from "@/service/BillingPlanService";
import LocationService from "@/service/LocationService";
import CompanyService from "@/service/CompanyService";
import AppointmentService from "@/service/AppointmentService";
import InvoiceService from "@/service/InvoiceService";
import ClientService from "@/service/ClientService";
import dayjs from "dayjs";
import PaymentService from "@/service/PaymentService";
import NoteTemplateService from "@/service/NoteTemplateService";
import ClientProvisionComponentService from "@/service/ClientProvisionComponentService";
import BankAccountService from "@/service/BankAccountService";
import MenuFinancialCompact from "@/components/menu/famodule/MenuFinancialCompact.vue";

export default {
    components: {
        MenuFinancialCompact,
        IsoEvolveFooter, MenuHealthCompact
    },
    setup() {
        const userStore = useUserStore()

        return {
            userStore
        }
    },
    created() {
        this.initFilters();

        this.standardFilters = [
            {field: 'billable', header: 'Billable'},
            {field: 'nonBillable', header: 'Non-Billable'},
            {field: 'personal', header: 'Personal'}
        ];
    },
    async mounted() {

        try {
            const userPingResponse = await UserService.getUserPing();
            console.log("[Appointments][mounted] System is active.");
            console.log("[Appointments][mounted] Data = ", userPingResponse);
        } catch (e) {
            console.log("[Appointments][mounted] System is NOT active.");
            console.log("[Appointments][mounted] Error = ", e);
            this.userStore.clearUser();
            this.$router.push("login");
            return;
        }

        if (!this.userStore.user.id) {
            console.log("[Appointments][mounted] User is NOT defined.");
            this.userStore.clearUser();
            this.$router.push('login');
            return;
        }

        await this.loadCompanyContext();
    },
    data() {

        return {
            totalRecords: 0,
            lazyParams: {
                first: 0,
                rows: 10,
                sortField: null,
                sortOrder: null,
                filters: {}
            },
            profile: {},
            activeTab: 1,
            company: {},
            companyConfiguration: {},
            userConfiguration: {},
            filters: null,
            submitted: false,
            note: {},
            noteTemplates: null,
            noteTemplateId: null,
            filteredPractitioners: [],
            filteredBankAccounts: [],
            filteredBillingPlans: [],
            filteredClients: [],
            filteredLocations: [],
            filteredProvisionComponents: [],
            filteredItemsCache: new Map(),
            appointment: {},
            appointments: null,
            originalAppointments: null,
            newAppointment: {},
            invoice: {},
            payment: {},
            billableAppointments: null,
            practitioners: [],
            locations: null,
            billingPlans: null,
            bankAccounts: [],
            clientProvisionComponents: [],
            confirmationResponse: 'N',
            selectedColumns: null,
            columnsDefault: null,
            columnsFullList: null,
            selectedStandardFilter: null,
            standardFilters: null,
            noteTypes: [
                {name: 'Personal', code: 'P'},
                {name: 'Clinical', code: 'C'},
            ],
            formatTypes: [
                {name: 'Rich Text', code: 'R'}
                // {name: 'Freeform', code: 'F'}
            ],
            statusTypes: [
                {name: 'Pending', code: 'E'},
                {name: 'Scheduled', code: 'S'},
                // {name: 'Invoiced', code: 'I'},
                {name: 'Missed', code: 'M'},
                {name: 'Completed', code: 'C'},
                {name: 'Booking', code: 'B'}
            ],
            confirmationTypes: [
                {name: 'Yes', code: 'Y'},
                {name: 'No', code: 'N'}
            ],
            invoiceTypes: [
                {name: 'Individual', code: 'I'},
                {name: 'Consolidated', code: 'C'}
            ],
            rateOptionTypes: [
                {name: 'Base Rate (Default)', code: 'D'},
                {name: 'Premier Rate A', code: 'A'},
                {name: 'Premier Rate B', code: 'B'}
            ],
            appointmentTypes: [
                // {name: 'All', code: 'A'},
                {name: 'Billable', code: 'B'},
                {name: 'Non-Billable', code: 'N'},
                {name: 'Personal', code: 'P'}
            ],
            frequencyTypes: [
                {name: 'None', code: 'N'},
                {name: 'Daily', code: 'D'},
                {name: 'Weekly', code: 'W'},
                {name: 'Bi-Weekly', code: 'B'},
                {name: 'Monthly', code: 'M'},
                {name: 'Annual', code: 'A'}
            ],
            paymentTypes: [
                {name: 'Cash', code: 'C'},
                {name: 'Bank Transfer', code: 'B'},
                {name: 'Credit Card', code: 'C'},
                {name: 'Healthcare Insurance', code: 'H'},
                {name: 'Cryptocurrency', code: 'R'},
                {name: 'Other', code: 'O'}
            ],
            submissionMethod: [
                {name: 'Invoice Only', code: 'C'},
                {name: 'Invoice and Health Insurance Claim', code: 'B'}
            ],
            healthInsuranceSubmissionMethod: [
                {name: 'Email', code: 'E'},
                {name: 'Electronic Data Interchange (EDI)', code: 'D'}
            ]
        }
    },
    computed: {
        parsedScheduled: {
            get() {
                return new Date(this.appointment.scheduled);
            },
            set(value) {
                this.appointment.scheduled = value instanceof Date ? value.toISOString() : '';
            }
        },
        computedGlobalFilterFields() {
            if (this.companyConfiguration.type === 'H') {
                return ['clientName', 'scheduled', 'billingPlanName', 'description', 'practitionerName'];
            } else {
                return ['clientName', 'scheduled', 'billingPlanName', 'description'];
            }
        },
        canCreateAppointment() {
            if (this.newAppointment.type === 'P') {
                if ((this.newAppointment.description) && (this.newAppointment.scheduled) &&
                    (this.newAppointment.status)) {
                    return true;
                } else {
                    return false;
                }
            } else if ((this.newAppointment.type === 'B') || (this.newAppointment.type === 'N')) {
                if ((this.newAppointment.client) && (this.newAppointment.scheduled) &&
                    (this.newAppointment.status) && (this.newAppointment.duration)) {
                    return true;
                } else {
                    return false;
                }
            } else {
                return true;
            }
        },
    },
    watch: {
        // Clear the cache when appointments data changes
        appointments() {
            this.filteredItemsCache.clear();
        },
        'userStore.activeCompanyId': {
            immediate: false,
            async handler(newCompanyId, oldCompanyId) {
                if (!newCompanyId || newCompanyId === oldCompanyId) {
                    return;
                }

                console.log(
                    '[Appointments][watch] Company changed:',
                    oldCompanyId,
                    '→',
                    newCompanyId
                );

                await this.loadCompanyContext();
            }
        }
    },
    methods: {
        async loadCompanyContext() {
            try {
                this.submitted = true;

                try {
                    // Attempt to get user data
                    const userData = await UserService.getUser(this.userStore.user.id);
                    this.profile = userData.data;
                } catch (e) {
                    console.error("[AppointmentsNew][mounted] Error fetching user data = ", e);
                    this.userStore.clearUser();
                    this.$router.push('login');
                    return;
                }

                this.profile.companyId = this.userStore.activeCompanyId;

                const [userConfigurationData, companyConfigurationData, companyData] = await Promise.all([
                    UserService.getUserConfig(this.userStore.user.id),
                    CompanyService.getCompanyConfiguration(this.profile.companyId),
                    CompanyService.getCompany(this.profile.companyId)
                ]);

                this.userConfiguration = userConfigurationData.data;
                this.companyConfiguration = companyConfigurationData.data;
                this.company = companyData.data;

                this.loadData();

                const [practitioners, locations, billingPlans, bankAccountData] = await Promise.all([
                    PractitionerService.getCompanyPractitioners(this.profile.companyId),
                    LocationService.getCompanyLocations(this.profile.companyId),
                    BillingPlanService.getActiveBillingPlans(this.profile.companyId),
                    BankAccountService.getBankAccountsForCompany(this.profile.companyId),
                ]);

                this.userStore.setPractitioners(practitioners.data);
                this.userStore.setLocations(locations.data);
                this.userStore.setBillingPlans(billingPlans.data);

                this.practitioners = practitioners.data;
                this.locations = locations.data;
                this.billingPlans = billingPlans.data;
                this.bankAccounts = bankAccountData.data;

                this.billingPlans = this.userStore.billingPlans;

                if (this.companyConfiguration.type === 'H') {
                    this.columnsDefault = [
                        {field: 'clientName', header: 'Client Name', sortable: true},
                        {field: 'clientClaimPreference', header: 'Claim Preference', sortable: true},
                        {field: 'billingPlanName', header: 'Billing Plan Name', sortable: true},
                        {field: 'practitionerName', header: 'Practitioner Name', sortable: true}
                    ];

                } else {
                    this.columnsDefault = [
                        {field: 'clientName', header: 'Client Name', sortable: true},
                        {field: 'clientClaimPreference', header: 'Claim Preference', sortable: true},
                        {field: 'billingPlanName', header: 'Billing Plan Name', sortable: true}
                    ];
                }

                if (this.companyConfiguration.type === 'H') {
                    this.columnsFullList = [
                        {field: 'clientName', header: 'Client Name', sortable: true},
                        {field: 'duration', header: 'Duration', sortable: true},
                        {field: 'clientClaimPreference', header: 'Claim Preference', sortable: true},
                        {field: 'billingPlanName', header: 'Billing Plan Name', sortable: true},
                        {field: 'practitionerName', header: 'Practitioner Name', sortable: true},
                        {field: 'invoiceUniqueId', header: 'Invoice #', sortable: true},
                        {field: 'locationName', header: 'Location', sortable: true},
                        {field: 'clientProvisionName', header: 'Provision', sortable: true},
                        {field: 'clientProvisionComponentName', header: 'Provision Component', sortable: true}
                    ];
                } else {
                    this.columnsFullList = [
                        {field: 'clientName', header: 'Client Name', sortable: true},
                        {field: 'duration', header: 'Duration', sortable: true},
                        {field: 'clientClaimPreference', header: 'Claim Preference', sortable: true},
                        {field: 'billingPlanName', header: 'Billing Plan Name', sortable: true},
                        {field: 'invoiceUniqueId', header: 'Invoice #', sortable: true},
                        {field: 'locationName', header: 'Location', sortable: true}
                    ];
                }

                this.selectedColumns = this.columnsDefault;

                this.submitted = false;

            } catch (e) {
                console.error("[Appointments][mounted] Error fetching additional data = ", e);
            }

            if (this.$route.query.new === "1") {
                this.callNewAppointment();
            }

            this.submitted = false;
            console.log("[Appointments][mounted] this.appointments = ", this.appointments);
            console.log("[Appointments][mounted] this.userConfiguration = ", this.userConfiguration);
            console.log("[Appointments][mounted] this.companyConfiguration = ", this.companyConfiguration);
            console.log("[Appointments][mounted] this.practitioners = ", this.practitioners);
            console.log("[Appointments][mounted] this.locations = ", this.locations);
            console.log("[Appointments][mounted] this.billingPlans = ", this.billingPlans);
            console.log("[Appointments][mounted] this.bankAccounts = ", this.bankAccounts);
            console.log("[Appointments][mounted] this.clientProvisionComponents = ", this.clientProvisionComponents);
        },
        async loadData() {
            this.submitted = true;
            try {
                console.log("[Appointments][loadData] Fetching clients with params:", this.lazyParams, "Search:", this.filters['global']?.value);

                if (this.userStore.user.rights === 'M') {
                    const response = await AppointmentService.getPractitionerAppointmentsLazyLoading(
                        this.userStore.user.practitionerId,
                        this.lazyParams,
                        this.filters['global']?.value || '',
                        this.selectedStandardFilter, null, null);

                    console.log("[Appointments][loadData] API Response:", response);
                    console.log("[Appointments][loadData] response.data.content:", response.data?.content);
                    console.log("[Appointments][loadData] response.data.totalElements:", response.data?.totalElements);

                    this.appointments = Array.isArray(response.data.content) ? response.data.content : [];
                    this.originalAppointments = this.appointments;

                    this.totalRecords = response.data?.totalElements || 0;

                    console.log("[Appointments][loadData] Clients Updated:", this.clients, "Total Records:", this.totalRecords);

                } else {
                    const response = await AppointmentService.getCompanyAppointmentsLazyLoading(
                        this.profile.companyId,
                        this.lazyParams,
                        this.filters['global']?.value || '',
                        this.selectedStandardFilter, null, null);

                    console.log("[Appointments][loadData] API Response:", response);
                    console.log("[Appointments][loadData] response.data.content:", response.data?.content);
                    console.log("[Appointments][loadData] response.data.totalElements:", response.data?.totalElements);

                    this.appointments = Array.isArray(response.data.content) ? response.data.content : [];
                    this.originalAppointments = this.appointments;

                    this.totalRecords = response.data?.totalElements || 0;

                    console.log("[Appointments][loadData] Clients Updated:", this.clients, "Total Records:", this.totalRecords);

                }
            } catch (error) {
                console.error("[Appointments][loadData] Error loading clients:", error);
                this.clients = [];
                this.totalRecords = 0;
            }
            this.submitted = false;
        },
        onPage(event) {
            console.log("[Appointments][onPage] Page Event:", event);
            this.lazyParams.first = event.first;
            this.lazyParams.rows = event.rows;
            this.loadData();
        },
        onSort(event) {
            this.lazyParams.sortField = event.sortField;
            this.lazyParams.sortOrder = event.sortOrder;
            this.loadData();
        },
        onFilter(event) {
            this.lazyParams.filters = event.filters;
            this.loadData();
        },
        onSearch() {
            console.log("[Appointments][onSearch] Searching with:", this.filters['global'].value);
            this.lazyParams.first = 0;
            this.loadData();
        },
        onToggleStandardFilters() {
            console.log("[Appointments][onToggleStandardFilters] Applying Standard Filter:", this.selectedStandardFilter);
            this.lazyParams.first = 0;
            this.loadData();
        },
        clearFilter() {
            this.initFilters();
            this.lazyParams.filters = {}; // Clear all column filters
            this.lazyParams.first = 0; // Reset pagination to first page
            this.loadData();
        },
        restoreToOriginal() {
            this.lazyParams = {
                first: 0,
                rows: 10,
                sortField: null,
                sortOrder: null,
                filters: {...this.originalFilters}, // Restore saved filters
            };
            this.loadData();
            window.location.reload();
        },
        initFilters() {
            this.filters = {
                'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
                'clientName': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'scheduled': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'billingPlanName': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'description': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'practitionerName': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
            }
        },
        callReset() {
            this.activeTab = 1;
        },
        async callNewAppointment() {
            this.newAppointment = {
                type: 'B',
                duration: 60,
                frequency: 'N',
                frequencyLength: 0,
                scheduled: moment().startOf('hour').toDate(),
                // scheduled: moment().toDate(),
                status: 'E',
                sendCalendarInvites: 'N',
                sendReminders: 'N',
                sendCreationReminder: 'N',
                sendMissedNotice: 'N',
                setupOnlineMeeting: 'N',
                requireNote: 'N',
                client: null,
                provision: null,
                practitioner: this.practitioners && this.practitioners.length > 0 ? this.practitioners[0] : null,
                location: this.locations && this.locations.length > 0 ? this.locations[0] : null,
                billingPlan: this.billingPlans && this.billingPlans.length > 0 ? this.billingPlans[0] : null
            };

            this.activeTab = 6;
        },
        onToggleColumns(value) {
            this.selectedColumns = this.columnsFullList.filter(col => value.includes(col));
        },
        getFilteredItems(data) {

            // console.log('[Appointments][getFilteredItems] Data = ', data);

            if (this.filteredItemsCache.has(data.id)) {
                return this.filteredItemsCache.get(data.id);
            }

            const items = [];

            if (data.status === 'S') {
                items.push({
                    label: 'Update',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callEditAppointment(data);
                    }
                });

                items.push({
                    label: 'Delete',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callDeleteAppointment(data);
                    }
                });

                if (data.setupOnlineMeeting === 'Y') {
                    items.push({
                        label: 'View Online Meeting',
                        class: 'mb-4 mr-4 ml-4 mt-4',
                        command: () => {
                            this.callOnlineMeeting(data);
                        }
                    });
                }

            } else if (data.status === 'C') {
                items.push({
                    label: 'Update',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callEditAppointment(data);
                    }
                });

                items.push({
                    label: 'Delete',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callDeleteAppointment(data);
                    }
                });

                if (data.setupOnlineMeeting === 'Y') {
                    items.push({
                        label: 'View Online Meeting',
                        class: 'mb-4 mr-4 ml-4 mt-4',
                        command: () => {
                            this.callOnlineMeeting(data);
                        }
                    });
                }

                if (data.type === 'B') {
                    if (this.userConfiguration.enableInvoicing !== 'N') {
                        items.push({
                            label: 'Generate Invoice',
                            class: 'mb-4 mr-4 ml-4 mt-4',
                            command: () => {
                                this.callInvoiceAppointment(data);
                            }
                        });
                    }
                }

            } else if (data.status === 'M') {
                items.push({
                    label: 'Update',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callEditAppointment(data);
                    }
                });

                items.push({
                    label: 'Delete',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callDeleteAppointment(data);
                    }
                });

                if (data.type === 'B') {
                    if (this.userConfiguration.enableInvoicing !== 'N') {
                        items.push({
                            label: 'Generate Invoice',
                            class: 'mb-4 mr-4 ml-4 mt-4',
                            command: () => {
                                this.callInvoiceAppointment(data);
                            }
                        });
                    }
                }

            } else if (data.status === 'I') {

                items.push({
                    label: 'View Invoice',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        console.log('[getFilteredItems][View Invoice] appointmentId = data.id = ', data.id);
                        this.$router.push({name: 'commonFinancialInvoices', query: {inputAppointmentId: data.id}});
                    }
                });

                if (data.type === 'B') {
                    if (this.userConfiguration.enableInvoicing !== 'N') {
                        items.push({
                            label: 'Allocate Payment',
                            class: 'mb-4 mr-4 ml-4 mt-4',
                            command: () => {
                                this.callAllocatePayment(data);
                            }
                        });
                    }
                }

            } else if (data.status === 'P') {
                items.push({
                    label: 'View Invoice',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        // this.callViewInvoice(data);
                        console.log('[getFilteredItems][View Invoice] appointmentId = data.id = ', data.id);
                        this.$router.push({name: 'commonFinancialInvoices', query: {inputAppointmentId: data.id}});
                    }
                });

            } else if ((data.status === 'E') || (data.status === 'B')) {
                items.push({
                    label: 'Update',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callEditAppointment(data);
                    }
                });

                items.push({
                    label: 'Delete',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callDeleteAppointment(data);
                    }
                });

            } else if (data.status === 'U') {
                items.push({
                    label: 'View Invoice',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        // this.callViewInvoice(data);
                        console.log('[getFilteredItems][View Invoice] appointmentId = data.id = ', data.id);
                        this.$router.push({name: 'commonFinancialInvoices', query: {inputAppointmentId: data.id}});
                    }
                });

            }

            this.filteredItemsCache.set(data.id, items);

            return items;
        },
        chooseBlankSelection(data) {
            console.log('[chooseBlankSelection] data = ', data);
            this.$toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Please select a valid option.',
                life: 3000
            });
        },
        async callCreateNote(data) {
            this.activeTab = 8;
            this.appointment = data;

            this.note = {};
            this.note.type = 'P';
            this.note.sharable = 'N';
            this.note.editable = 'N';
            this.note.format = 'R';
            this.note.status = 'A';
            this.note.description = '';

            const noteTemplateData = await NoteTemplateService.getCompanyNoteTemplates(this.profile.companyId);
            this.noteTemplates = noteTemplateData.data;
        },
        searchPractitioners(event) {
            console.log("[Appointments][searchPractitioners] event.query = ", event.query);

            const q = (event?.query ?? '').trim().toLowerCase();

            // If dropdown was clicked with an empty input, show all (or a cached subset)
            if (!q) {
                // Option A: use an already-fetched list
                this.filteredPractitioners = (this.practitioners ?? []).slice(0, 50); // cap if large
                return;
            }

            PractitionerService.searchCompanyPractitioners(this.profile.companyId, q)
                .then(data => {
                    console.log("[Appointments][searchPractitioners] Getting practitioner data for company = ", this.profile.companyId);
                    this.filteredPractitioners = data.data;
                    console.log("[Appointments][searchPractitioners] Got practitioner data = ", data.data);
                })
                .catch(e => {
                    console.log("[Appointments][searchPractitioners] Could not get practitioner data for company = ", this.profile.companyId);
                    console.log("[Appointments][searchPractitioners] Error = ", e);
                    this.filteredPractitioners = [];
                });

        },
        searchBankAccounts(event) {
            console.log("[Appointments][searchBankAccounts] event.query = ", event.query);

            const q = (event?.query ?? '').trim().toLowerCase();

            // If dropdown was clicked with an empty input, show all (or a cached subset)
            if (!q) {
                // Option A: use an already-fetched list
                this.filteredBankAccounts = (this.bankAccounts ?? []).slice(0, 50); // cap if large
                return;
            }

            BankAccountService.searchCompanyBankAccounts(this.profile.companyId, q)
                .then(data => {
                    console.log("[Appointments][searchBankAccounts] Getting bank account data for company = ",
                        this.profile.companyId);
                    this.filteredBankAccounts = data.data;
                    console.log("[Appointments][searchBankAccounts] Got bank account data = ", data.data);
                })
                .catch(e => {
                    console.log("[Appointments][searchBankAccounts] Could not get bank account data for company = ",
                        this.profile.companyId);
                    console.log("[Appointments][searchBankAccounts] Error = ", e);
                    this.filteredBankAccounts = [];
                });

        },
        async onClientSelected(event) {
            const client = event.value;
            console.log("[Appointments][onClientSelected] Client selected:", client);

            this.clientProvisionComponents = [];

            if (client?.id) {
                try {
                    const response = await ClientProvisionComponentService
                        .getClientProvisionComponentsForClient(client.id);

                    this.clientProvisionComponents = response.data ?? [];
                    console.log("[Appointments][onClientSelected] Loaded provision components:", this.clientProvisionComponents);
                } catch (e) {
                    console.log("[Appointments][onClientSelected] Error loading provision components:", e);
                    this.clientProvisionComponents = [];
                }
            }

            // Reset provision component selection if no data
            if (this.clientProvisionComponents.length === 0) {
                this.newAppointment.provisionComponent = null;
            }
        },
        async searchProvisionComponentsOnCreateAppointment(event) {
            const q = (event?.query ?? "").trim().toLowerCase();

            console.log("[searchProvisionComponentsOnCreateAppointment] q =", q);

            if (!this.clientProvisionComponents || this.clientProvisionComponents.length === 0) {
                try {
                    const response = await ClientProvisionComponentService
                        .getClientProvisionComponentsForClient(this.newAppointment.client.id);

                    this.clientProvisionComponents = response.data ?? [];

                    console.log("[searchProvisionComponentsOnCreateAppointment] Loaded provisions:", this.clientProvisionComponents);
                } catch (e) {
                    console.log("[searchProvisionComponentsOnCreateAppointment] Error loading provisions:", e);
                    this.clientProvisionComponents = [];
                }
            }

            if (!q) {
                this.filteredProvisionComponents = this.clientProvisionComponents.slice(0, 50);
                return;
            }

            this.filteredProvisionComponents = this.clientProvisionComponents
                .filter(pc => pc.fullName?.toLowerCase().includes(q))
                .slice(0, 50);

            console.log("[searchProvisionComponentsOnCreateAppointment] Filtered:", this.filteredProvisionComponents);
        },
        async searchProvisionComponentsOnUpdateAppointment(event) {
            const q = (event?.query ?? "").trim().toLowerCase();

            console.log("[searchProvisionComponentsOnCreateAppointment] q =", q);

            if (!this.clientProvisionComponents || this.clientProvisionComponents.length === 0) {
                try {
                    const response = await ClientProvisionComponentService
                        .getClientProvisionComponentsForClient(this.appointment.clientId);

                    this.clientProvisionComponents = response.data ?? [];

                    console.log("[searchProvisionComponentsOnCreateAppointment] Loaded provisions:", this.clientProvisionComponents);
                } catch (e) {
                    console.log("[searchProvisionComponentsOnCreateAppointment] Error loading provisions:", e);
                    this.clientProvisionComponents = [];
                }
            }

            if (!q) {
                this.filteredProvisionComponents = this.clientProvisionComponents.slice(0, 50);
                return;
            }

            this.filteredProvisionComponents = this.clientProvisionComponents
                .filter(pc => pc.fullName?.toLowerCase().includes(q))
                .slice(0, 50);

            console.log("[searchProvisionComponentsOnCreateAppointment] Filtered:", this.filteredProvisionComponents);
        },
        searchBillingPlan(event) {
            console.log("[Appointments][searchBillingPlan] event.query = ", event.query);
            console.log("[Appointments][searchBillingPlan] this.filteredBillingPlans = ", this.filteredBillingPlans);
            console.log("[Appointments][searchBillingPlan] Getting billing plan data for company = ", this.profile.companyId);

            if (event.query === '') {
                console.log("[Appointments][searchBillingPlan] event.query is blank. So calling getActiveBillingPlans.");
                BillingPlanService.getActiveBillingPlans(this.profile.companyId)
                    .then(data => {
                        this.filteredBillingPlans = data.data;
                        console.log("[Appointments][searchBillingPlan] Got billing plan data = ", data.data);
                    })
                    .catch(e => {
                        console.log("[Appointments][searchBillingPlan] Could not get billing plan data for user = ", this.profile.companyId);
                        console.log("[Appointments][searchBillingPlan] Error = ", e);
                    });
            } else {
                console.log("[Appointments][searchBillingPlan] event.query is NOT blank. So calling searchBillingPlans.");
                BillingPlanService.searchBillingPlans(this.profile.companyId, event.query.toLowerCase())
                    .then(data => {
                        this.filteredBillingPlans = data.data;
                        console.log("[Appointments][searchBillingPlan] Got billing plan data = ", data.data);
                    })
                    .catch(e => {
                        console.log("[Appointments][searchBillingPlan] Could not get billing plan data for user = ", this.profile.companyId);
                        console.log("[Appointments][searchBillingPlan] Error = ", e);
                    });
            }

        },
        searchClients(event) {
            console.log("[Appointments][searchClients] event.query = ", event.query);

            ClientService.searchCompanyClientsActive(this.profile.companyId, event.query.toLowerCase())
                .then(data => {
                    console.log("[Appointments][searchClients] Getting client data for company = ", this.profile.companyId);
                    this.filteredClients = data.data;
                    console.log("[Appointments][searchClients] Got client data = ", data.data);
                })
                .catch(e => {
                    console.log("[Appointments][searchClients] Could not get client data for user = ", this.profile.companyId);
                    console.log("[Appointments][searchClients] Error = ", e);
                });

        },
        searchLocations(event) {
            console.log("[Appointments][searchLocations] event.query = ", event.query);

            const q = (event?.query ?? '').trim().toLowerCase();

            if (!q) {
                // Option A: use an already-fetched list
                this.filteredLocations = (this.locations ?? []).slice(0, 50); // cap if large
                return;
            }

            LocationService.searchCompanyLocations(this.profile.companyId, event.query.toLowerCase())
                .then(data => {
                    console.log("[Appointments][searchLocations] Getting location data for company = ", this.profile.companyId);
                    this.filteredLocations = data.data;
                    console.log("[Appointments][searchLocations] Got location data = ", data.data);
                })
                .catch(e => {
                    this.filteredLocations = [];

                    console.log("[Appointments][searchLocations] Could not get location data for user = ", this.profile.companyId);
                    console.log("[Appointments][searchLocations] Error = ", e);
                });

        },
        startMeeting() {
            console.log("[Appointments][startMeeting] Start.");
            console.log("[Appointments][startMeeting] this.appointment.onlineMeetingHostUrl = ", this.appointment.onlineMeetingHostUrl);

            window.open(this.appointment.onlineMeetingHostUrl, '_blank');

        },
        sendMeetingDetails() {
            console.log("[Appointments][sendMeetingDetails] Start.");
        },
        async updateAppointmentAndFetchData() {

            this.submitted = true;

            try {
                if (this.appointment.type.trim()) {

                    console.log("[Appointments][updateAppointmentAndFetchData] this.appointment.clientName = ", this.appointment.clientName);
                    console.log("[Appointments][updateAppointmentAndFetchData] this.appointment.practitionerName = ", this.appointment.practitionerName);
                    console.log("[Appointments][updateAppointmentAndFetchData] this.appointment.billingPlanName = ", this.appointment.billingPlanName);
                    console.log("[Appointments][updateAppointmentAndFetchData] this.appointment.locationName = ", this.appointment.locationName);
                    console.log("[Appointments][updateAppointmentAndFetchData] this.appointment.clientProvisionComponentName = ", this.appointment.clientProvisionComponentName);

                    var tempClient = this.appointment.clientName;
                    var tempDescription = this.appointment.description;
                    var tempPractitioner = this.appointment.practitionerName;
                    var tempBillingPlan = this.appointment.billingPlanName;
                    var tempLocation = this.appointment.locationName;
                    var tempProvisionComponent = this.appointment.clientProvisionComponentName;

                    console.log("[Appointments][updateAppointmentAndFetchData] BEFORE. tempDescription = ", tempDescription);
                    console.log("[Appointments][updateAppointmentAndFetchData] BEFORE. tempClient = ", tempClient);
                    console.log("[Appointments][updateAppointmentAndFetchData] BEFORE. tempPractitioner = ", tempPractitioner);
                    console.log("[Appointments][updateAppointmentAndFetchData] BEFORE. tempBillingPlan = ", tempBillingPlan);
                    console.log("[Appointments][updateAppointmentAndFetchData] BEFORE. tempLocation = ", tempLocation);
                    console.log("[Appointments][updateAppointmentAndFetchData] BEFORE. tempProvisionComponent = ", tempProvisionComponent);

                    if (this.appointment.type !== 'P') {

                        try {
                            if (this.appointment.clientName.id == null) {
                                console.log("[Appointments][updateAppointmentAndFetchData] NULL CASE. BEFORE. tempClient = ", tempClient);
                                tempClient = this.appointment.clientId;
                                console.log("[Appointments][updateAppointmentAndFetchData] NULL CASE. AFTER. tempClient = ", tempClient);
                                tempDescription = this.appointment.description;
                                console.log("[Appointments][updateAppointmentAndFetchData] NULL CASE. AFTER. tempDescription = ", tempDescription);
                            } else {
                                console.log("[Appointments][updateAppointmentAndFetchData] INSIDE. BEFORE. tempClient = ", tempClient);
                                tempClient = this.appointment.clientName.id;
                                console.log("[Appointments][updateAppointmentAndFetchData] INSIDE. AFTER. tempClient = ", tempClient);
                                tempDescription = this.appointment.clientName.name;
                                console.log("[Appointments][updateAppointmentAndFetchData] INSIDE. AFTER. tempDescription = ", tempDescription);
                            }
                        } catch (error) {
                            console.log("[Appointments][updateAppointmentAndFetchData] EXCEPTION. BEFORE. tempClient = ", tempClient);
                            tempClient = this.appointment.clientId;
                            console.log("[Appointments][updateAppointmentAndFetchData] EXCEPTION. AFTER. tempClient = ", tempClient);
                            tempDescription = this.appointment.description;
                            console.log("[Appointments][updateAppointmentAndFetchData] EXCEPTION. AFTER. tempDescription = ", tempDescription);
                        }

                        try {
                            if (this.appointment.practitionerName.id == null) {
                                console.log("[Appointments][updateAppointmentAndFetchData] NULL CASE. BEFORE. tempPractitioner = ", tempPractitioner);
                                tempPractitioner = this.appointment.practitionerId;
                                console.log("[Appointments][updateAppointmentAndFetchData] NULL CASE. AFTER. tempPractitioner = ", tempPractitioner);
                            } else {
                                console.log("[Appointments][updateAppointmentAndFetchData] INSIDE. BEFORE. tempPractitioner = ", tempPractitioner);
                                tempPractitioner = this.appointment.practitionerName.id;
                                console.log("[Appointments][updateAppointmentAndFetchData] INSIDE. AFTER. tempPractitioner = ", tempPractitioner);
                            }
                        } catch (error) {
                            tempPractitioner = this.appointment.practitionerId;
                        }

                        try {
                            if (this.appointment.billingPlanName.id == null) {
                                tempBillingPlan = this.appointment.billingPlanId;
                            } else {
                                tempBillingPlan = this.appointment.billingPlanName.id;
                            }

                        } catch (error) {
                            tempBillingPlan = this.appointment.billingPlanId;
                        }

                        try {
                            if (this.appointment.locationName.id == null) {
                                tempLocation = this.appointment.locationId;
                            } else {
                                tempLocation = this.appointment.locationName.id;
                            }
                        } catch (error) {
                            tempLocation = this.appointment.locationId;
                        }

                        try {
                            if (!this.appointment.clientProvisionComponentName) {
                                console.log("[Appointments][updateAppointmentAndFetchData] OUTSIDE. BEFORE. tempProvisionComponent = ", tempProvisionComponent);
                                tempProvisionComponent = null;
                                console.log("[Appointments][updateAppointmentAndFetchData] OUTSIDE. AFTER. tempProvisionComponent = ", tempProvisionComponent);
                            } else {
                                if (this.appointment.clientProvisionComponentName.id == null) {
                                    console.log("[Appointments][updateAppointmentAndFetchData] NULL CASE. BEFORE. tempProvisionComponent = ", tempProvisionComponent);
                                    tempProvisionComponent = this.appointment.clientProvisionComponentId;
                                    console.log("[Appointments][updateAppointmentAndFetchData] NULL CASE. AFTER. tempProvisionComponent = ", tempProvisionComponent);
                                } else {
                                    console.log("[Appointments][updateAppointmentAndFetchData] INSIDE. BEFORE. tempProvisionComponent = ", tempProvisionComponent);
                                    tempProvisionComponent = this.appointment.clientProvisionComponentName.id;
                                    console.log("[Appointments][updateAppointmentAndFetchData] INSIDE. AFTER. tempProvisionComponent = ", tempProvisionComponent);
                                }
                            }
                        } catch (error) {
                            tempProvisionComponent = this.appointment.clientProvisionComponentId;
                            console.log("[Appointments][updateAppointmentAndFetchData] CATCH. tempProvisionComponent = ", tempProvisionComponent);
                        }

                    }

                    console.log("[Appointments][updateAppointmentAndFetchData] AFTER. tempDescription = ", tempDescription);
                    console.log("[Appointments][updateAppointmentAndFetchData] AFTER. tempClient = ", tempClient);
                    console.log("[Appointments][updateAppointmentAndFetchData] AFTER. tempPractitioner = ", tempPractitioner);
                    console.log("[Appointments][updateAppointmentAndFetchData] AFTER. tempBillingPlan = ", tempBillingPlan);
                    console.log("[Appointments][updateAppointmentAndFetchData] AFTER. tempLocation = ", tempLocation);
                    console.log("[Appointments][updateAppointmentAndFetchData] AFTER. tempProvisionComponent = ", tempProvisionComponent);

                    var tz = this.getTimezone(this.companyConfiguration?.region) || "Europe/London";
                    const m = moment(this.appointment.scheduled);

                    console.log("[Appointments][createAppointment] tz = ", tz);

                    var newAppointmentDto = {
                        type: this.appointment.type,
                        clientId: tempClient,
                        practitionerId: tempPractitioner,
                        billingPlanId: tempBillingPlan,
                        locationId: tempLocation,
                        clientProvisionComponentId: tempProvisionComponent,
                        description: tempDescription,
                        frequency: this.appointment.frequency,
                        frequencyLength: this.appointment.frequencyLength,
                        duration: this.appointment.duration,
                        scheduled: m.format('YYYY-MM-DD HH:mm'),
                        utcScheduled: moment.tz(m.format('YYYY-MM-DD HH:mm'), tz).toISOString(),
                        status: this.appointment.status,
                        sendCalendarInvites: this.appointment.sendCalendarInvites,
                        sendReminders: this.appointment.sendReminders,
                        sendMissedNotice: this.appointment.sendMissedNotice,
                        setupOnlineMeeting: this.appointment.setupOnlineMeeting,
                        sequenceCount: this.appointment.sequenceCount,
                        excludedDays: this.appointment.excludedDays
                    };

                    console.log("[Appointments][updateAppointmentAndFetchData] Before creating appointment back-end call. Data = ", newAppointmentDto);

                    // Update the appointment
                    const updateResponse = await AppointmentService.updateAppointment(this.appointment.id, newAppointmentDto);
                    console.log(`[Appointments][updateAppointmentAndFetchData] Data = `, updateResponse);

                    this.loadData();

                    // // Fetch company appointments after updating the appointment
                    // const appointmentsResponse = await AppointmentService.getCompanyAppointments(this.profile.companyId);
                    // console.log(`[Appointments][updateAppointmentAndFetchData] Got appointment data = `, appointmentsResponse);
                    //
                    // this.appointments = appointmentsResponse.data;
                    // this.originalAppointments = appointmentsResponse.data;
                    // this.userStore.setAppointments(appointmentsResponse.data);

                    // Show a success message
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Appointment Updated',
                        life: 3000
                    });

                    this.activeTab = 1;
                }

            } catch (e) {
                console.error('[Appointments][updateAppointmentAndFetchData] Error:', e);

                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to update appointment. Please try again.',
                    life: 3000
                });
            }

            this.activeTab = 1;
            this.submitted = false;

        },
        async editAppointment() {
            console.log("[Appointments][editAppointment] Start.");
            this.submitted = true;

            await this.updateAppointmentAndFetchData();

            this.submitted = false;
        },
        async deleteAppointment() {
            console.log("[Appointment][deleteAppointment] Start.");

            this.submitted = true;

            if (this.confirmationResponse.trim() === "Y") {

                if (this.appointment.id) {

                    try {
                        // Delete the appointment
                        const response = await AppointmentService.deleteAppointment(this.appointment.id);
                        console.log('[Appointments][deleteAppointment] Delete response = ', response);

                        this.loadData();

                        // // Fetch company appointments
                        // const appointmentsResponse = await AppointmentService.getCompanyAppointments(this.profile.companyId);
                        // console.log('[Appointments][deleteAppointment] Ge appointments response = ', appointmentsResponse);
                        //
                        // this.appointments = appointmentsResponse.data;
                        // this.originalAppointments = appointmentsResponse.data;
                        // this.userStore.setAppointments(appointmentsResponse.data);

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Deleted appointment',
                            life: 3000
                        });
                    } catch (error) {
                        console.error('[Appointments][deleteAppointment] Error:', error);

                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to delete appointment. Please try again.',
                            life: 3000
                        });
                    }
                }

            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async callEditAppointment(appointment) {
            this.appointment = {...appointment};

            console.log("[Appointment][callEditAppointment] Appointment Data = ", this.appointment);

            try {
                const response = await ClientProvisionComponentService
                    .getClientProvisionComponentsForClient(this.appointment.clientId);

                this.clientProvisionComponents = response.data ?? [];
                console.log("[Appointments][callEditAppointment] Loaded provision components:", this.clientProvisionComponents);
            } catch (e) {
                console.log("[Appointments][callEditAppointment] Error loading provision components:", e);
                this.clientProvisionComponents = [];
            }

            this.activeTab = 2;

            console.log("[Appointment][callEditAppointment] this.activeTab = ", this.activeTab);
        },
        callDeleteAppointment(appointment) {
            this.appointment = {...appointment};
            this.confirmationResponse = 'N';

            this.activeTab = 3;

            console.log("[Appointment][callDeleteAppointment] this.activeTab = ", this.activeTab);

        },
        callOnlineMeeting(appointment) {
            this.appointment = {...appointment};

            console.log("[Appointment][callOnlineMeeting] Appointment Data = ", this.appointment);

            this.activeTab = 4;
        },
        async callInvoiceAppointment(appointment) {
            this.appointment = {...appointment};

            console.log("[Appointment][callInvoiceAppointment] Appointment Data = ", this.appointment);

            this.invoice = {};

            this.invoice.appointmentId = this.appointment.id;
            this.invoice.clientId = this.appointment.clientId;
            this.invoice.billingPlanId = this.appointment.billingPlanId;
            this.invoice.billingPlanName = this.appointment.billingPlanName;
            this.invoice.submissionMethod = 'C';
            this.invoice.useSystemProcedureRates = 'N';
            this.invoice.useSystemProductRates = 'N';
            this.invoice.rateOption = 'D';
            this.invoice.healthInsuranceSubmission = 'E';
            this.invoice.useTaxRate = this.company.taxRegistered;
            this.invoice.useAutomaticSubmission = 'N';
            this.invoice.type = 'I';
            this.invoice.bankAccount = this.bankAccounts && this.bankAccounts.length > 0 ? this.bankAccounts[0] : null

            this.invoice.invoiced = this.appointment.scheduled;

            const startDate = moment().startOf('month');
            const endDate = moment(this.appointment.scheduled)
                .set({ hour: 23, minute: 59, second: 0, millisecond: 0 });

            this.invoice.startDate = startDate.format('YYYY-MM-DD HH:mm');
            this.invoice.endDate = endDate.format('YYYY-MM-DD HH:mm');

            try {
                const [billingPlanData] = await Promise.all([
                    BillingPlanService.getBillingPlan(this.appointment.billingPlanId)
                ]);

                this.billingPlan = billingPlanData.data;
                console.log("[Appointments][mounted] billingPlanData.data = ", billingPlanData.data);

                this.invoice.useSystemProcedureRates = this.billingPlan.useSystemProcedureRates;
                this.invoice.useSystemProductRates = this.billingPlan.useSystemProductRates;
                this.invoice.healthInsuranceSubmission = this.billingPlan.healthInsuranceSubmission;
                this.invoice.invoiced = this.appointment.scheduled;

                console.log("[Appointment][callInvoiceAppointment] Invoice Data = ", this.invoice);

            } catch (e) {
                console.log("[Appointment][callInvoiceAppointment] Could not get billing plan = ", this.appointment.billingPlanId);
                console.log("[Appointment][callInvoiceAppointment] Error = ", e);
            }

            if (this.appointment.clientMedicalName === null) {
                this.invoice.useSystemProcedureRates = 'N';
                this.invoice.submissionMethod = 'C';
            }

            this.activeTab = 5;
        },
        async callAllocatePayment(appointment) {
            this.appointment = {...appointment};

            try {
                const [resultsData] = await Promise.all([
                    InvoiceService.getInvoiceforAppointment(this.appointment.id)
                ]);

                this.invoice = resultsData.data;

            } catch (e) {
                console.log("[Appointment][callInvoiceAppointment] Error = ", e);
            }

            this.payment = {};
            this.payment.amount = this.invoice.amount;
            this.payment.type = 'B';
            this.payment.paid = moment().toDate();
            // this.payment.paid.setMinutes(0);

            this.activeTab = 7;
        },
        async allocatePayment() {
            this.submitted = true;

            console.log("[Appointment][allocatePayment] this.payment = ", this.payment);

            if (this.payment.description) {

                var newPaymentDto = {
                    description: this.payment.description,
                    type: this.payment.type,
                    amount: this.invoice.amount - this.invoice.paid,
                    paid: dayjs(this.payment.paid, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm')
                };

                console.log("[Appointment][payInvoice] newPaymentDto = ", newPaymentDto);

                try {
                    const response = await PaymentService.createPaymentForInvoice(this.profile.companyId, this.invoice.id, newPaymentDto);
                    console.log('[Appointments][deleteAppointment] Response = ', response);

                    this.loadData();

                    // Fetch company appointments
                    // const appointmentsResponse = await AppointmentService.getCompanyAppointments(this.profile.companyId);
                    // console.log('[Appointments][deleteAppointment] Ge appointments response = ', appointmentsResponse);
                    //
                    // this.appointments = appointmentsResponse.data;
                    // this.originalAppointments = appointmentsResponse.data;
                    // this.userStore.setAppointments(appointmentsResponse.data);

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Allocated payment for invoice',
                        life: 3000
                    });
                } catch (error) {
                    console.error('[Appointments][deleteAppointment] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to allocate payment for invoice. Please try again.',
                        life: 3000
                    });
                }

            }

            this.activeTab = 1;
            this.submitted = false;
        },
        sleep(ms) {
            return new Promise(res => setTimeout(res, ms));
        },
        async invoiceAppointment() {

            if (this.submitted) return;

            this.submitted = true;

            console.log("[Appointment][invoiceAppointment] AFTER. tempBillingPlan = ", tempBillingPlan);

            try {
                // await this.sleep(10000);

                console.log("[Appointment][invoiceAppointment] this.invoice = ", this.invoice);

                var tempBillingPlan = this.invoice.billingPlanName;
                console.log("[Appointment][invoiceAppointment] BEFORE. tempBillingPlan = ", tempBillingPlan);

                try {
                    if (this.invoice.billingPlanName.id == null) {
                        tempBillingPlan = this.invoice.billingPlanId;
                    } else {
                        tempBillingPlan = this.invoice.billingPlanName.id;
                    }

                } catch (error) {
                    tempBillingPlan = this.invoice.billingPlanId;
                }

                var tempBankAccount = this.invoice.bankAccount;
                console.log("[Appointment][invoiceAppointment] BEFORE. tempBankAccount = ", tempBankAccount);

                try {
                    if (this.invoice.bankAccount.id == null) {
                        tempBankAccount = this.invoice.bankAccountId;
                    } else {
                        tempBankAccount = this.invoice.bankAccount.id;
                    }

                } catch (error) {
                    tempBankAccount = this.invoice.bankAccountId;
                }

                if (this.invoice.invoiced) {

                    var newInvoiceDto = {
                        type: this.invoice.type,
                        invoiced: moment(this.invoice.invoiced).format('YYYY-MM-DD HH:mm'),
                        startDate: moment(this.invoice.startDate).format('YYYY-MM-DD HH:mm'),
                        endDate: moment(this.invoice.endDate).format('YYYY-MM-DD HH:mm'),
                        userId: this.profile.id,
                        billingPlanId: tempBillingPlan,
                        bankAccountId: tempBankAccount,
                        appointmentId: this.invoice.appointmentId,
                        clientId: this.invoice.clientId,
                        submissionMethod: this.invoice.submissionMethod,
                        healthInsuranceSubmission: this.invoice.healthInsuranceSubmission,
                        useSystemProcedureRates: this.invoice.useSystemProcedureRates,
                        useSystemProductRates: 'N',
                        useTaxRate: this.invoice.useTaxRate,
                        useAutomaticSubmission: this.invoice.useAutomaticSubmission,
                        rateOption: this.invoice.rateOption
                    };

                    console.log("[" + name + "[invoiceAppointment] Before creating invoice back-end call. Data = ", newInvoiceDto);

                    const response = await InvoiceService.createInvoice(this.profile.companyId, newInvoiceDto);
                    console.log('[Appointments][invoiceAppointment] Response = ', response);

                    this.loadData();

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Created invoice for appointment',
                        life: 3000
                    });
                }
            } catch (error) {
                console.error('[Appointments][invoiceAppointment] Error:', error);

                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to create invoice for appointment. Please try again.',
                    life: 3000
                });
            } finally {
                this.submitted = false;          // ALWAYS reset
            }

            this.activeTab = 1;
            this.submitted = false;

        },
        getTimezone(region) {
            switch ((region || "").toUpperCase()) {
                case "ZA":
                    return "Africa/Johannesburg";
                case "UK":
                case "GB":
                    return "Europe/London";
                case "US":
                case "USA":
                case "EST":
                    return "America/New_York";
                case "CA":
                case "CANADA":
                    return "America/Toronto";
                case "IN":
                case "INDIA":
                    return "Asia/Kolkata";
                case "NA":
                case "NAMIBIA":
                    return "Africa/Windhoek";
                case "BW":
                case "BOTSWANA":
                    return "Africa/Gaborone";
                default:
                    return "Europe/London";
            }
        },
        async createAppointment() {
            console.log("[Appointment][deleteAppointment] Start.");

            this.submitted = true;

            if (this.newAppointment.type) {

                try {
                    console.log("[Appointments][saveAppointment] this.newAppointment.client = ", this.newAppointment.client);
                    console.log("[Appointments][saveAppointment] this.newAppointment.practitioner = ", this.newAppointment.practitioner);
                    console.log("[Appointments][saveAppointment] this.newAppointment.billingPlan = ", this.newAppointment.billingPlan);
                    console.log("[Appointments][saveAppointment] this.newAppointment.location = ", this.newAppointment.location);
                    console.log("[Appointments][saveAppointment] this.newAppointment.provisionComponent = ", this.newAppointment.provisionComponent);

                    var tempDescription = this.newAppointment.description;
                    var tempClient = "";
                    var tempPractitioner = "";
                    var tempBillingPlan = "";
                    var tempLocation = "";
                    var tempProvisionComponent = "";

                    if (this.newAppointment.type !== 'P') {

                        try {
                            tempClient = this.newAppointment.client.id;
                            tempDescription = this.newAppointment.client.name;
                        } catch (error) {
                            tempClient = "";
                        }

                        try {
                            tempPractitioner = this.newAppointment.practitioner.id;
                        } catch (error) {
                            tempPractitioner = "";
                        }

                        try {
                            tempBillingPlan = this.newAppointment.billingPlan.id;
                        } catch (error) {
                            tempBillingPlan = "";
                        }

                        try {
                            tempLocation = this.newAppointment.location.id;
                        } catch (error) {
                            tempLocation = "";
                        }

                        try {
                            tempProvisionComponent = this.newAppointment.provisionComponent.id;
                        } catch (error) {
                            tempProvisionComponent = "";
                        }
                    }

                    var tz = this.getTimezone(this.companyConfiguration?.region) || "Europe/London";
                    const m = moment(this.newAppointment.scheduled);

                    console.log("[Appointments][createAppointment] tz = ", tz);

                    var newAppointmentDto = {
                        type: this.newAppointment.type,
                        clientId: tempClient,
                        practitionerId: tempPractitioner,
                        billingPlanId: tempBillingPlan,
                        locationId: tempLocation,
                        clientProvisionComponentId: tempProvisionComponent,
                        description: tempDescription,
                        frequency: this.newAppointment.frequency,
                        frequencyLength: this.newAppointment.frequencyLength,
                        duration: this.newAppointment.duration,
                        status: this.newAppointment.status,
                        scheduled: m.format('YYYY-MM-DD HH:mm'),
                        utcScheduled: moment.tz(m.format('YYYY-MM-DD HH:mm'), tz).toISOString(),
                        sendCalendarInvites: this.newAppointment.sendCalendarInvites,
                        sendReminders: this.newAppointment.sendReminders,
                        sendCreationReminder: this.newAppointment.sendCreationReminder,
                        sendMissedNotice: this.newAppointment.sendMissedNotice,
                        setupOnlineMeeting: this.newAppointment.setupOnlineMeeting,
                        requireNote: this.newAppointment.requireNote
                    };

                    console.log("[Appointments][saveAppointment] Before creating appointment back-end call. Data = ", newAppointmentDto);

                    const response = await AppointmentService.createAppointment(this.profile.companyId, newAppointmentDto);
                    console.log('[Appointments][deleteAppointment] Delete response = ', response);

                    this.loadData();

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Created appointment',
                        life: 3000
                    });
                } catch (error) {
                    console.error('[Appointments][deleteAppointment] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to create appointment. Please try again.',
                        life: 3000
                    });
                }
            }

            this.newAppointment = {
                type: 'B',
                duration: 60,
                frequency: 'N',
                frequencyLength: 0,
                scheduled: moment().toDate(),
                status: 'E',
                sendCalendarInvites: 'N',
                sendReminders: 'N',
                sendCreationReminder: 'N',
                sendMissedNotice: 'N',
                setupOnlineMeeting: 'N',
                client: null,
                practitioner: this.practitioners && this.practitioners.length > 0 ? this.practitioners[0] : null,
                location: this.locations && this.locations.length > 0 ? this.locations[0] : null,
                billingPlan: this.billingPlans && this.billingPlans.length > 0 ? this.billingPlans[0] : null,
                bankAccount: this.bankAccounts && this.bankAccounts.length > 0 ? this.bankAccounts[0] : null
            };

            this.activeTab = 1;
            this.submitted = false;
        }
    }
    ,
}
;
</script>

<style scoped lang="scss">
a {
    text-decoration: none;
}

::v-deep(.p-datatable-frozen-tbody) {
    font-weight: bold;
}

::v-deep(.p-datatable-scrollable .p-frozen-column) {
    font-weight: bold;
}

::v-deep(.p-progressbar) {
    height: .5rem;
    background-color: #D8DADC;

    .p-progressbar-value {
        background-color: #607D8B;
    }
}

.status-badge, .type-badge {
    border-radius: var(--border-radius);
    padding: .25em .5rem;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: .3px;
}

.status-badge {
    &.status-E {
        background: #9da5d9;
        color: #080c61;
    }

    &.status-S {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-C {
        background: #FFCDD2;
        color: #C63737;
    }

    &.status-M {
        background: #FEEDAF;
        color: #8A5340;
    }

    &.status-I {
        background: #B3E5FC;
        color: #23547B;
    }

    &.status-P {
        background: #ECCFFF;
        color: #694382;
    }

    &.status-U {
        background: #d9b78a;
        color: #b94f03;
    }

    &.status-B {
        background: #b9a1de;
        color: #830293;
    }

}

.type-badge {
    &.status-P {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-B {
        background: #FFCDD2;
        color: #C63737;
    }

    &.status-N {
        background: #FEEDAF;
        color: #8A5340;
    }

    &.status-E {
        background: #B3E5FC;
        color: #23547B;
    }

    &.status-S {
        background: #ECCFFF;
        color: #694382;
    }

    &.status-W {
        background: #d9b78a;
        color: #b94f03;
    }

    &.status-I {
        background: #b9a1de;
        color: #830293;
    }

    &.status-Y {
        background: #C8E6C9;
        color: #256029;
    }

}


.tabview-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-tabview p {
    line-height: 1.5;
    margin: 0;
}

.accordion-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-accordion p {
    line-height: 1.5;
    margin: 0;
}

.mb-2 {
    margin-bottom: 1rem;
}

.mr-2 {
    margin-right: 1rem;
}

.p-tooltip-text {
    white-space: nowrap;
}

</style>
